"use client";

import { createContext, useContext, useState, type ReactNode } from 'react';

interface SidebarContextType {
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
  /**
   * Whether the shell's header strip (DashboardHeader — the notification
   * bell and the mobile drawer toggle) is mounted.
   *
   * Separate from `isVisible` on purpose: several pages hide the sidebar to
   * get more width while still scrolling inside the shell, and those keep
   * the bell. This one is for the pages that paint themselves over the whole
   * viewport, where a 48px strip they have to start below is chrome they
   * cannot use anyway.
   */
  isHeaderVisible: boolean;
  setIsHeaderVisible: (visible: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [isVisible, setIsVisible] = useState(true);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  return (
    <SidebarContext.Provider value={{ isVisible, setIsVisible, isHeaderVisible, setIsHeaderVisible }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
} 